/**
 * AnchorAttestation route — Phase H landing page for the two-sided
 * email-change attestation links sent via send-anchor-change-attestations.
 *
 * URL: /anchor-attestation?side=old|new&token=<plaintext>&action=reject?
 *
 * Doctrine (sacred/email-change-attestation.md §VI):
 *   The click IS the gesture of attestation. The page mirrors that:
 *   no re-entry friction, contemplative confirmation, sigil-warm copy.
 *
 * Five surfaces:
 *   1. Loading            — token submitted, awaiting RPC
 *   2. Attested (one side)— "you attest the gesture" + "awaiting the other side"
 *   3. Attested (both)    — "It is so." (status='both_attested')
 *   4. Rejected           — "the gesture returns to silence"
 *   5. Error              — token expired / not recognized / already used
 *
 * The user does NOT need to be authenticated to land here. The RPCs
 * are token-gated (SHA-256 hash match), not auth.uid()-gated.
 */

import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

type AttestationState =
  | { kind: 'loading' }
  | { kind: 'attested_one_side'; side: 'old' | 'new' }
  | { kind: 'attested_both' }
  | { kind: 'rejected'; side: 'old' | 'new' }
  | { kind: 'error'; message: string };

export default function AnchorAttestation() {
  const [params] = useSearchParams();
  const side = params.get('side');
  const token = params.get('token');
  const action = params.get('action');

  const [state, setState] = useState<AttestationState>({ kind: 'loading' });

  useEffect(() => {
    if (!side || (side !== 'old' && side !== 'new') || !token) {
      setState({
        kind: 'error',
        message: 'This link is missing required parameters. The gesture cannot be recognized.',
      });
      return;
    }

    let cancelled = false;
    (async () => {
      try {
        if (action === 'reject') {
          const { error } = await supabase
            .schema('core')
            .rpc('reject_anchor_change', { p_token: token, p_side: side });
          if (cancelled) return;
          if (error) throw new Error(error.message);
          setState({ kind: 'rejected', side });
          return;
        }

        const rpcName = side === 'old' ? 'attest_old_anchor' : 'attest_new_anchor';
        const { data, error } = await supabase
          .schema('core')
          .rpc(rpcName, { p_token: token });
        if (cancelled) return;
        if (error) throw new Error(error.message);

        const result = data as { bothAttested?: boolean };
        if (result?.bothAttested) {
          setState({ kind: 'attested_both' });
        } else {
          setState({ kind: 'attested_one_side', side });
        }
      } catch (err) {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'Unexpected error.';
        setState({ kind: 'error', message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [side, token, action]);

  return (
    <Shell>
      {state.kind === 'loading' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The Line gathers the gesture
          </p>
          <h1 className="display text-3xl md:text-4xl font-medium italic text-ink/70 leading-tight">
            Recognizing your attestation...
          </h1>
        </>
      )}

      {state.kind === 'attested_one_side' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            One side attested
          </p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">Your gesture</span>
            <br />
            is recorded.
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            {state.side === 'old'
              ? 'The old anchor has attested. The Line awaits the new address.'
              : 'The new anchor has attested. The Line awaits the old address.'}
          </p>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto">
            The swap completes only when both sides have attested. Until then,
            the current anchor remains active. The proposal expires 24 hours
            after it was made.
          </p>
          <HomeLink />
        </>
      )}

      {state.kind === 'attested_both' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            Both sides have attested
          </p>
          <h1 className="display text-5xl md:text-6xl font-medium leading-[1.05]">
            <span className="italic text-accent">It is</span>
            <br />
            so.
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            The duet is complete. The new anchor binds the Orchist's record;
            the body of memory holds the moment of swap.
          </p>
          <HomeLink />
        </>
      )}

      {state.kind === 'rejected' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The gesture is refused
          </p>
          <h1 className="display text-4xl md:text-5xl font-medium leading-[1.05]">
            <span className="italic text-accent">It will</span>
            <br />
            not be.
          </h1>
          <p className="text-lg text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            You have refused this attestation. The Orchist's current anchor
            stays active. The proposal will not complete.
          </p>
          <HomeLink />
        </>
      )}

      {state.kind === 'error' && (
        <>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-ink/50">
            The gesture cannot be received
          </p>
          <h1 className="display text-3xl md:text-4xl font-medium leading-[1.1]">
            <span className="italic text-accent">The link</span>{' '}
            <br />
            did not resolve.
          </h1>
          <p className="text-base text-ink/70 leading-relaxed max-w-md mx-auto font-display italic">
            {state.message}
          </p>
          <p className="text-sm text-ink/55 italic font-display max-w-md mx-auto leading-relaxed">
            Common reasons: the link expired (24-hour TTL), the proposal was
            cancelled, or this attestation has already been gathered. Open The
            Line and check Connections for the current state of any pending
            swap.
          </p>
          <HomeLink />
        </>
      )}
    </Shell>
  );
}

function HomeLink() {
  return (
    <p className="text-sm text-ink/50 pt-6">
      <Link
        to="/"
        className="underline underline-offset-4 decoration-ink/20 hover:decoration-ink/60 hover:text-ink"
      >
        Open The Line
      </Link>
    </p>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <main className="min-h-screen flex flex-col justify-center items-center px-6 py-24">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.05]"
        style={{
          backgroundImage:
            'radial-gradient(circle at 30% 20%, #8b7ab8 0%, transparent 55%), radial-gradient(circle at 70% 80%, #c9a961 0%, transparent 55%)',
        }}
      />
      <div className="container max-w-xl text-center space-y-6 relative">{children}</div>
    </main>
  );
}
